import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Alert,
  CardContent,
  Grid,
  Link,
  Button as MuiButton,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography as MuiTypography,
  useTheme,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import agent from "../../utils/axios";
import { Authenticate } from "../../types/Authenticate";
import { hasAnyRole } from "../../utils/auth";
import CompanySelectDialog, { QuickBooksCompany } from "./CompanySelectDialog";

interface AuthUrlResponse {
  url: string;
}

const Card = styled(MuiCard)`
  ${spacing};
  box-shadow: none;
  background-color: ${(props) => props.theme.palette.background.paper};
  color: ${(props) => props.theme.palette.text.primary};
`;

const Divider = styled(MuiDivider)(spacing);

const Shadow = styled.div`
  box-shadow: ${(props) => props.theme.shadows[1]};
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Button = styled(MuiButton)(spacing);

const Typography = styled(MuiTypography)(display);

type PayeeDataSchema = {
  [key: string]: any;
  id: string;
  name: {
    first: string;
    middle: string;
    last: string;
  };
  mailingAddress: {
    address1: string;
    address2: string;
    address3: string;
    city: string;
    state: string;
    postalCode: string;
  };
  phone: {
    home: string;
    cell: string;
  };
  tin: string;
  last4: string;
  email: string;
  type: string;
  paymentMethod: string;
  payable: boolean;
  bills: [
    {
      requestingApplication: string;
      applicantId: string | null;
      requestingClient: string;
      programName: string;
      amount: number;
      paymentMethod: string;
      status: string;
      invoiceDate: string;
      payTo: string;
      paymentService: {
        billDate: string;
        paymentDate: string;
        paymentStatus: string;
        billRefCode: string;
        paymentRefCode: string;
      };
      address: {
        address1: string;
        address2: string;
        address3: string;
        city: string;
        state: string;
        postalCode: string;
      };
      notes: string;
      name: string;
    }
  ];
};

function BillDetails() {
  const { billRefCode } = useParams();
  const [payeeData, setPayeeData] = useState<PayeeDataSchema | null>(null);
  const [loading, setLoading] = React.useState(false);
  // AB#5377: the API refuses a BillRefCode carried by more than one bill (409) rather than
  // returning an arbitrary one. Without somewhere to put that, the component rendered an empty
  // receipt — blank amount, blank payee, and today's date in place of the payment date.
  const [loadError, setLoadError] = useState<string | null>(null);
  const theme = useTheme();
  const canConvertToCheck = hasAnyRole(["Admin", "Developer"]);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);

  // Fetch the bill and update state. Reused for the initial load and to refresh
  // the displayed status after Convert to Check (no full-page reload).
  const refreshBill = useCallback(async () => {
    setLoadError(null);
    const response = await agent.Bills.getBillByRefCode(billRefCode ?? "");
    setPayeeData(response);
  }, [billRefCode]);

  useEffect(() => {
    setLoading(true);
    refreshBill()
      .catch((error) => {
        console.error("Error fetching payee:", error);
        setPayeeData(null);
        setLoadError(
          error?.response?.data?.message ??
            error?.data?.message ??
            "This bill could not be loaded."
        );
      })
      .finally(() => setLoading(false));
  }, [refreshBill]);

  // Step 1: open the QuickBooks company selector.
  const handleConvertToCheck = () => {
    setCompanyDialogOpen(true);
  };

  // Step 2: user picked a company. If we already have a valid token for that
  // company, send the bill straight to QBO; otherwise authenticate it first.
  const handleCompanyConfirm = async (company: QuickBooksCompany) => {
    setCompanyDialogOpen(false);
    setLoading(true);
    try {
      // Try CSIPay's OWN stored token for the selected company first — no popup. The API
      // auto-refreshes an expired access token. If the company has no usable token, the API
      // returns requiresAuth (no bills touched) and we pop the QB sign-in for that company.
      const result = await agent.Bills.convertToCheck(
        [billRefCode ?? ""],
        company.realmId
      );
      if (result?.requiresAuth) {
        // startQuickBooksAuth → handleAuthCode authenticates, retries the convert, refreshes,
        // and clears loading, so hand off and return without clearing loading here.
        await startQuickBooksAuth(company.realmId);
        return;
      }
      // Converted with the stored token; update the status in place (now Check / Paid).
      await refreshBill();
      setLoading(false);
    } catch (error) {
      console.error("Error converting to check:", error);
      setLoading(false);
    }
  };

  // Step 2b: OAuth popup flow for a company without a usable token.
  const startQuickBooksAuth = async (realmId: string) => {
    try {
      const { url }: AuthUrlResponse = await agent.quickbooks.getAuthUrl();
      if (!url) {
        setLoading(false);
        return;
      }
      const urlParams = new URLSearchParams(new URL(url).search);
      const redirect_uri = urlParams.get("redirect_uri") ?? "";
      const popupWidth = 600;
      const popupHeight = 700;
      const left = window.screen.width / 2 - popupWidth / 2;
      const top = window.screen.height / 2 - popupHeight / 2;

      const authWindow = window.open(
        url,
        "QuickBooks Login",
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`
      );

      // Check if the popup opened successfully
      if (!authWindow) {
        setLoading(false);
        return;
      }

      const authCheckInterval = setInterval(() => {
        try {
          if (authWindow.location.href.includes("code=")) {
            const params = new URLSearchParams(authWindow.location.search);
            const code = params.get("code");
            if (code) {
              clearInterval(authCheckInterval);
              authWindow.close();
              // Use the company the user SELECTED in the dialog — not whatever QB returns on
              // the callback — so the bill goes to the chosen company.
              handleAuthCode(code, redirect_uri, realmId);
            }
          }
        } catch (error) {
          // Ignore cross-origin errors until the popup is redirected to your domain with the code.
        }

        if (authWindow.closed) {
          clearInterval(authCheckInterval);
        }
      }, 200);
    } catch (error) {
      setLoading(false);
    }
  };

  // Step 3: exchange the auth code, then send the bill to the selected company.
  const handleAuthCode = async (
    code: string | null,
    redirect_uri: string,
    realmId: string
  ) => {
    if (code) {
      try {
        const authRequest: Authenticate = { code };

        await agent.quickbooks.authenticateQuickbooks(
          authRequest,
          redirect_uri,
          realmId
        );
        await agent.Bills.convertToCheck([billRefCode ?? ""], realmId);
        // Refresh just the bill so the status updates in place (now Check / Paid).
        await refreshBill();
      } catch (error) {
        console.error("Error processing QuickBooks authentication:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div
        style={{
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.default,
        }}
      >
        Loading...
      </div>
    );
  }

  // AB#5377: show why the bill could not be loaded instead of an empty receipt.
  if (loadError || !payeeData) {
    return (
      <React.Fragment>
        <Helmet title="Bill Details" />

        <Typography variant="h3" gutterBottom display="inline">
          Bill #{billRefCode}
        </Typography>

        <Breadcrumbs aria-label="Breadcrumb" mt={2}>
          <Link
            component={NavLink}
            to="/bills"
            sx={{ color: theme.palette.primary.main }}
          >
            Bills
          </Link>
          <Typography sx={{ color: theme.palette.text.primary }}>
            Details
          </Typography>
        </Breadcrumbs>

        <Divider my={6} />

        <Alert severity="warning">
          {loadError ?? "This bill could not be loaded."}
        </Alert>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="Bill Details" />

      <Typography variant="h3" gutterBottom display="inline">
        Bill #{billRefCode}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link
          component={NavLink}
          to="/bills"
          sx={{ color: theme.palette.primary.main }}
        >
          Bills
        </Link>
        <Typography sx={{ color: theme.palette.text.primary }}>
          Details
        </Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Grid container justifyContent="center">
        <Grid item xs={12} lg={10}>
          <Shadow>
            <Card px={6} pt={6}>
              <CardContent>
                <Grid container spacing={6}>
                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                      This is the receipt for a payment of{" "}
                      {payeeData?.bills?.[0]?.amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}{" "}
                      (USD) made to {payeeData?.name?.first}{" "}
                      {payeeData?.name?.last}.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption">Payment Ref Code.</Typography>
                    <Typography variant="body2">
                      {payeeData?.bills?.[0]?.paymentService?.paymentRefCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" align="right" display="block">
                      Payment Date
                    </Typography>
                    <Typography variant="body2" align="right">
                      {new Intl.DateTimeFormat("en-US", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(
                        payeeData?.bills?.[0]?.paymentService?.paymentDate
                          ? new Date(
                              payeeData.bills[0].paymentService!.paymentDate
                            )
                          : undefined
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption">Payment To:</Typography>
                    <Typography variant="body2">
                      {payeeData?.name?.first} {payeeData?.name?.middle}{" "}
                      {payeeData?.name?.last}
                      <br />
                      <br />
                      {payeeData?.bills?.[0]?.payTo ||
                        payeeData?.bills?.[0]?.name}
                      <br />
                      {payeeData?.bills?.[0]?.address?.address1}{" "}
                      {payeeData?.bills?.[0]?.address?.address2}
                      <br />
                      {payeeData?.bills?.[0]?.address?.city}
                      <br />
                      {payeeData?.bills?.[0]?.address?.state}
                      <br />
                      {payeeData?.bills?.[0]?.address?.postalCode}
                      <br />
                      <Link href={`mailto:${payeeData?.email}`}>
                        {payeeData?.email}
                      </Link>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" align="right" display="block">
                      Client
                    </Typography>
                    <Typography variant="body2" align="right">
                      {payeeData?.bills[0].requestingApplication}
                      <br />
                      {payeeData?.bills[0].requestingClient}
                      <br />
                      {payeeData?.bills[0].programName}
                    </Typography>
                    <br />
                    <Typography variant="caption" align="right" display="block">
                      Payment Type
                    </Typography>
                    <Typography variant="body2" align="right">
                      {" "}
                      {payeeData?.bills[0]?.paymentMethod}
                    </Typography>
                    <Typography
                      variant="caption"
                      align="right"
                      display="block"
                      mt={2}
                    >
                      Payment Status
                    </Typography>
                    <Typography variant="body2" align="right">
                      {" "}
                      {payeeData?.bills?.[0]?.paymentService?.paymentStatus}
                    </Typography>
                  </Grid>
                  {payeeData?.bills[0]?.notes && (
                    <Grid item xs={12}>
                      <Divider />
                      <Typography variant="caption" display="block" mt={2}>
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {payeeData?.bills[0]?.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            <Card px={6}>
              <Table
                sx={{
                  "& .MuiTableCell-root": {
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Program</TableCell>
                    <TableCell />
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{payeeData?.bills[0].programName}</TableCell>
                    <TableCell />
                    <TableCell align="right">
                      {payeeData?.bills?.[0]?.amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell />
                    <TableCell>Total</TableCell>
                    <TableCell align="right">
                      {payeeData?.bills?.[0]?.amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
            <Card pb={6} px={6}>
              <CardContent style={{ textAlign: "center" }}>
                {canConvertToCheck &&
                payeeData?.bills[0]?.paymentMethod.toLowerCase() === "ach" ? (
                  <Button
                    variant="contained"
                    color="primary"
                    mt={2}
                    onClick={handleConvertToCheck}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    Convert to Check
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          </Shadow>
        </Grid>
      </Grid>

      <CompanySelectDialog
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        onConfirm={handleCompanyConfirm}
      />
    </React.Fragment>
  );
}

export default BillDetails;
